import { useState } from "react";
import { Badge, Popover, Button, Typography, Empty } from "antd";
import {
  BellOutlined,
  CheckOutlined,
  CloudUploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  BugOutlined,
  TrophyOutlined,
  CreditCardOutlined,
  UnlockOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNotifications } from "../hooks/useNotifications";

const { Text } = Typography;

const PALETTE = {
  success: { color: "#059669", bg: "#ecfdf5" },
  error:   { color: "#dc2626", bg: "#fef2f2" },
  warning: { color: "#d97706", bg: "#fffbeb" },
  info:    { color: "#4f46e5", bg: "#eef2ff" },
};

// Backend messages start with an emoji; pick an icon from the wording instead
const ICON_RULES = [
  [/award|bonus|milestone/i, TrophyOutlined, "warning"],
  [/lifted/i, UnlockOutlined, "success"],
  [/banned|ban\b/i, StopOutlined, "error"],
  [/warning/i, WarningOutlined, "warning"],
  [/reject|declin|fail/i, CloseCircleOutlined, "error"],
  [/payment|paid/i, CreditCardOutlined, "success"],
  [/error report|report/i, BugOutlined, "info"],
  [/follower/i, TeamOutlined, "info"],
  [/extension|extended|expir/i, ClockCircleOutlined, "info"],
  [/approved|success/i, CheckCircleOutlined, "success"],
  [/submitted|upload|new /i, CloudUploadOutlined, "info"],
];

const EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}️‍⃣]/gu;

const cleanMessage = (msg = "") => msg.replace(EMOJI, "").replace(/\s{2,}/g, " ").trim();

const styleFor = (n, text) => {
  const rule = ICON_RULES.find(([re]) => re.test(text));
  const Icon = rule ? rule[1] : BellOutlined;
  const tone = n.type === "error" ? "error" : rule ? rule[2] : n.type;
  return { Icon, ...(PALETTE[tone] ?? PALETTE.info) };
};

// Bold the quoted part, e.g. New variation submitted: "jerome"
const renderMessage = (text) =>
  text.split(/("[^"]+")/).map((part, i) =>
    /^"[^"]+"$/.test(part) ? (
      <Text strong key={i} style={{ fontSize: 13 }}>
        {part.slice(1, -1)}
      </Text>
    ) : (
      part
    ),
  );

const timeAgo = (date) => {
  const d = new Date(date);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "Just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

export default function NotificationBell({ role = "user" }) {
  const [open, setOpen] = useState(false);
  const { notifications, unreadCount, markAllRead } = useNotifications(role);

  const handleOpen = (visible) => {
    setOpen(visible);
    if (visible && unreadCount > 0) markAllRead();
  };

  const content = (
    <div style={{ width: 340 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "2px 2px 10px",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 6,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text strong style={{ fontSize: 15 }}>Notifications</Text>
          {unreadCount > 0 && (
            <span
              style={{
                background: "#4f46e5",
                color: "#fff",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: 10,
                padding: "1px 8px",
              }}
            >
              {unreadCount} new
            </span>
          )}
        </div>
        {notifications.length > 0 && (
          <Button size="small" type="text" icon={<CheckOutlined />} onClick={markAllRead}
            style={{ color: "#4f46e5", fontSize: 12 }}>
            Mark all read
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <Empty
          image={<BellOutlined style={{ fontSize: 36, color: "#c7d2fe" }} />}
          styles={{ image: { height: 44 } }}
          description={<Text type="secondary">You're all caught up</Text>}
          style={{ padding: "16px 0" }}
        />
      ) : (
        <div style={{ maxHeight: 380, overflowY: "auto", margin: "0 -4px", padding: "0 4px" }}>
          {notifications.map((n, i) => {
            const text = cleanMessage(n.message);
            const { Icon, color, bg } = styleFor(n, text);
            return (
              <div
                key={n._id ?? n.id ?? i}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                  padding: "10px 10px",
                  borderRadius: 10,
                  marginBottom: 2,
                  background: n.read ? "transparent" : "#f8f7ff",
                  borderLeft: `3px solid ${n.read ? "transparent" : color}`,
                  transition: "background 0.15s",
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: bg,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 17,
                    flexShrink: 0,
                  }}
                >
                  <Icon />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, lineHeight: 1.45, color: "#1f2937" }}>
                    {renderMessage(text)}
                  </div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11 }}
                    title={new Date(n.createdAt).toLocaleString()}
                  >
                    {timeAgo(n.createdAt)}
                  </Text>
                </div>
                {!n.read && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: color,
                      marginTop: 6,
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={handleOpen}
      placement="bottomRight"
      styles={{ body: { borderRadius: 14, padding: 14 } }}
    >
      <Badge count={unreadCount} size="small">
        <Button
          type="text"
          icon={<BellOutlined style={{ fontSize: 20 }} />}
          style={{ display: "flex", alignItems: "center" }}
        />
      </Badge>
    </Popover>
  );
}
